import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import { FeatureFlag } from 'app/api/services/site-configs/types';

import { SsrModule } from 'app/constants/ssrModules';
import { useTranslation } from 'app/lang';
import { useFeatureFlag } from 'app/lib/feature-flags';
import useAdultAge from 'app/lib/region/useAdultAge';
import {
  getBrandCopyright,
  getBrandSocials,
  getCompanyLicense,
  getPaymentMethods,
} from 'app/packages/brand';

import DocumentModals from './components/DocumentModals';
import { MobileBanner } from './components/MobileBanner';
import PaymentMethods from './components/PaymentMethods';
import SocialIcons from './components/SocialIcons';

import dictionary from './Footer.dictionary.json';

import styles from './Footer.module.scss';

type FooterContentProps = {
  className?: string;
};

const FooterContent: FC<FooterContentProps> = ({
  className,
}) => {
  const { t } = useTranslation();
  const isAllowWholeFooter = useFeatureFlag(FeatureFlag.ALLOW_WHOLE_FOOTER);
  const adultAge = useAdultAge();

  const copyrightText = getBrandCopyright();
  const socialIcons = getBrandSocials();
  const paymentMethods = getPaymentMethods();
  const companyLicenseTitle = getCompanyLicense();

  const _className = classNames(
    styles.footerContainer,
    className,
  );

  return (
    <footer data-ssr-id={SsrModule.FOOTER}
            className={_className}
            data-test="footer">
      <DocumentModals />

      <ul className={styles.headerMenuList}>
        <li className={styles.headerListItem}>
          <Link data-ga-event="footer-privacyPolicy"
                to="?privacy_policy">
            {t(dictionary.linkPrivacyPolicy)}
          </Link>
        </li>
        <li className={styles.headerListItem}>
          <Link data-ga-event="footer-AMLManual"
                to="?aml_manual">
            {t(dictionary.linkAMLManual)}
          </Link>
        </li>
        <li className={styles.headerListItem}>
          <Link data-ga-event="footer-generalTerms"
                to="?general_terms">
            {t(dictionary.linkGeneralTerms)}
          </Link>
        </li>
        <li className={styles.headerListItem}>
          <Link data-ga-event="footer-responsibleGaming"
                to="?responsible_gaming">
            {t(dictionary.linkResponsibleGaming)}
          </Link>
        </li>
        <li className={styles.headerListItem}>
          <Link data-ga-event="footer-termsOfService"
                to="?terms_of_service">
            {t(dictionary.linkTermsOfService)}
          </Link>
        </li>
      </ul>

      {isAllowWholeFooter && (
        <>
          <MobileBanner />

          <div className={styles.informationContainer}>
            {socialIcons && (
              <SocialIcons socialIcons={socialIcons} />
            )}

            {paymentMethods && (
              <PaymentMethods paymentMethods={paymentMethods} />
            )}
          </div>

          <div className={styles.infoBlock}>
            {companyLicenseTitle && (
              <p>
                {companyLicenseTitle}
              </p>
            )}
            <p>
              {copyrightText}
              {' '}
              {adultAge && t(dictionary.infoCopyright, { age: adultAge })}
            </p>
          </div>
        </>
      )}
    </footer>
  );
};

export default FooterContent;
